/**
 * Design tokens for MafertSystem UI.
 * Semantic values aligned to Figma Mafert-System variables (Gate 2 AppShell).
 * Prefer colorTokens.semantic.* over raw hex in UI code.
 */

export const colorTokens = {
  brand: {
    primary: '#a7292f',
    primaryLight: '#c43a40',
    primaryDark: '#7a1c21',
    secondary: '#d8d8d8',
    secondaryLight: '#ffffff',
    secondaryDark: '#8a8a8a',
  },
  neutral: {
    white: '#ffffff',
    background: '#050505',
    surface: '#141414',
    border: '#333333',
    textPrimary: '#ffffff',
    textSecondary: '#d8d8d8',
    textDisabled: 'rgba(255, 255, 255, 0.38)',
  },
  semantic: {
    bg: {
      canvas: '#050505',
      surface: '#141414',
      surfaceSelected: '#211013',
      sidebar: '#090909',
      topbar: '#0f0f0f',
      input: '#0f0f0f',
    },
    text: {
      primary: '#ffffff',
      secondary: '#d8d8d8',
      muted: '#8a8a8a',
      disabled: 'rgba(255, 255, 255, 0.38)',
      inverse: '#111111',
    },
    border: {
      default: '#333333',
      subtle: '#2a2a2a',
    },
    action: {
      primary: {
        bg: '#ffffff',
        border: '#ffffff',
        text: '#111111',
      },
      secondary: {
        bg: '#1b1b1b',
        border: '#333333',
        text: '#ffffff',
      },
    },
    status: {
      brand: {
        bg: '#211013',
        border: '#a7292f',
        text: '#ffffff',
      },
      neutral: {
        bg: '#171717',
        border: '#333333',
        text: '#d8d8d8',
      },
    },
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
    info: '#0288d1',
  },
};

export const typographyTokens = {
  fontFamily: '"Nunito Sans", "Helvetica", "Arial", sans-serif',
  fontFamilyDisplay: '"Montserrat", "Nunito Sans", "Helvetica", "Arial", sans-serif',
  fontFamilyMono: '"Roboto Mono", "Courier New", monospace',
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,
  pageTitle: {
    variant: 'h4',
    fontWeight: 700,
  },
  sectionTitle: {
    variant: 'h6',
    fontWeight: 600,
  },
  body: {
    variant: 'body1',
  },
  caption: {
    variant: 'body2',
  },
};

export const spacingTokens = {
  unit: 8,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  pageGap: 24,
  sectionGap: 16,
  cardPadding: 16,
  tableCellPaddingX: 16,
  tableCellPaddingY: 8,
};

export const radiusTokens = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
  card: 16,
  dialog: 12,
  chip: 16,
  navItem: 10,
  search: 12,
  iconButton: 10,
};

/** Dark UI scrollbar for table/data scroll containers (not body/page chrome). */
export const scrollbarTokens = {
  size: 8,
  track: 'transparent',
  thumb: 'rgba(167, 41, 47, 0.6)',
  thumbHover: colorTokens.brand.primary,
  thumbRadius: 999,
};

export const shadowTokens = {
  none: 'none',
  sm: '0px 1px 3px rgba(0, 0, 0, 0.4)',
  md: '0px 2px 6px rgba(0, 0, 0, 0.45)',
  lg: '0px 4px 12px rgba(0, 0, 0, 0.5)',
  appBar: 'none',
  card: 'none',
};

export const layoutTokens = {
  sidebarWidth: 240,
  sidebarCollapsedWidth: 72,
  topbarHeight: 72,
  /** Default internal admin content rail (detail / form / standard pages). */
  contentMaxWidth: 1136,
  /** Opt-in rail for data-heavy list pages (e.g. Provisioning readiness). */
  contentMaxWidthDataList: 1560,
  pagePadding: 4,
  cardRadius: 16,
  drawerZIndexOffset: 1,
  /** Permanent collapsed rail between these inclusive CSS px bounds. */
  sidebarCollapseMinWidth: 1024,
  sidebarCollapseMaxWidth: 1279,
};

export const statusTokens = {
  customer: {
    active: 'success',
    suspended: 'error',
  },
  tenant: {
    active: 'success',
    suspended: 'error',
    provisioning: 'warning',
    trial: 'info',
  },
  subscription: {
    active: 'success',
    trial: 'info',
    trialing: 'info',
    past_due: 'warning',
    payment_blocked: 'error',
    canceled: 'default',
    expired: 'default',
  },
  payment: {
    pending: 'warning',
    paid: 'success',
    failed: 'error',
    refunded: 'default',
  },
  support: {
    open: 'info',
    waiting_for_support: 'warning',
    waiting_for_customer: 'info',
    resolved: 'success',
    closed: 'default',
    // Legacy Slovak values (pre-H5.1)
    Vytvorený: 'default',
    'V riešení': 'warning',
    'Čaká sa': 'info',
    'Čaká sa na odpoveď': 'info',
    Pozastavený: 'error',
    Vyriešený: 'success',
  },
  readiness: {
    not_ready: 'error',
    partially_ready: 'warning',
    ready_for_manual_review: 'info',
    ready: 'success',
    passed: 'success',
    not_applicable: 'default',
  },
  invitation: {
    pending: 'warning',
    invited: 'info',
    accepted: 'success',
    expired: 'default',
    revoked: 'default',
  },
  plan: {
    active: 'success',
    inactive: 'default',
  },
  generic: {
    active: 'success',
    inactive: 'default',
    enabled: 'success',
    disabled: 'default',
  },
  provisioning_run: {
    pending: 'warning',
    running: 'info',
    completed: 'success',
    failed: 'error',
    canceled: 'default',
    skipped: 'default',
    rolled_back: 'warning',
  },
  provisioning_step: {
    pending: 'warning',
    running: 'info',
    completed: 'success',
    failed: 'error',
    skipped: 'default',
  },
  health_check: {
    ok: 'success',
    warning: 'warning',
    failed: 'error',
    skipped: 'default',
  },
  provisioning_status: {
    record_only: 'info',
    pending: 'warning',
    provisioned: 'success',
    failed: 'error',
    disabled: 'default',
  },
  application_readiness: {
    preparing: 'warning',
    failed: 'error',
    ready: 'success',
    simulated: 'default',
  },
  schema_version: {
    draft: 'warning',
    approved: 'success',
    deprecated: 'default',
    blocked: 'error',
  },
  schema_plan: {
    draft: 'warning',
    ready: 'info',
    blocked: 'error',
    approved: 'success',
    executed: 'success',
    canceled: 'default',
  },
  schema_risk: {
    low: 'success',
    medium: 'warning',
    high: 'error',
    destructive: 'error',
    unknown: 'default',
  },
};

export const priorityTokens = {
  low: 'default',
  normal: 'default',
  high: 'warning',
  blocker: 'error',
};

const flatStatusMap = Object.values(statusTokens).reduce((acc, group) => ({
  ...acc,
  ...group,
}), {});

export function getStatusColor(status, category = 'auto') {
  if (!status) {
    return 'default';
  }

  if (category !== 'auto' && statusTokens[category]?.[status]) {
    return statusTokens[category][status];
  }

  if (flatStatusMap[status]) {
    return flatStatusMap[status];
  }

  if (priorityTokens[status]) {
    return priorityTokens[status];
  }

  return 'default';
}

export function getPriorityColor(priority) {
  if (!priority) {
    return 'default';
  }

  return priorityTokens[priority] || 'default';
}

export function getStatusCategories() {
  return Object.keys(statusTokens);
}
